// import React, { useEffect, useMemo, useState, useCallback } from "react";
// import { Icon } from "@iconify/react";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import { NotificationManager } from "react-notifications";
// import { COPPER_SECTIONS, fmtDate, fmtFileDate } from "./copperConfig";
// import { copperApi, COPPER_BASE } from "./copperApi";
// import { useTimezone } from "../../hook/useTimezone";
// import AppDatePicker from "../AppDatePicker";

// const PAGE_SIZES = [10, 25, 50, 100];

// const CopperLayer = ({ section }) => {
//   const config = COPPER_SECTIONS[section];
//   // Subscribe to timezone changes so column render closures (which call
//   // fmtDate) re-run with the latest tz.
//   useTimezone();

//   const [rawRows, setRawRows] = useState([]);
//   const [meta, setMeta] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [refreshTick, setRefreshTick] = useState(0);

//   const initialFilters = useMemo(() => {
//     const out = {};
//     (config?.filters || []).forEach((f) => {
//       out[f.key] = f.defaultValue ?? "";
//     });
//     return out;
//   }, [config]);

//   const [filters, setFilters] = useState(initialFilters);
//   const [fromDate, setFromDate] = useState("");
//   const [toDate, setToDate] = useState("");
//   const [search, setSearch] = useState("");
//   const [pageSize, setPageSize] = useState(25);
//   const [page, setPage] = useState(1);

//   // Reset state when switching sections.
//   useEffect(() => {
//     setFilters(initialFilters);
//     setFromDate("");
//     setToDate("");
//     setSearch("");
//     setPage(1);
//   }, [section, initialFilters]);

//   const fetchData = useCallback(async () => {
//     if (!config) return;
//     setLoading(true);
//     setError(null);
//     try {
//       const params = {};
//       Object.entries(filters).forEach(([k, v]) => {
//         if (v !== "" && v !== undefined && v !== null) params[k] = v;
//       });
//       if (config.supportsDateRange) {
//         if (fromDate) params.fromDate = fromDate;
//         if (toDate) params.toDate = toDate;
//       }
//       const res = await copperApi.get(config.endpoint, { params });
//       const data = res.data || {};
//       const rows = Array.isArray(data[config.listKey]) ? data[config.listKey] : [];
//       setRawRows(rows);
//       const { [config.listKey]: _omit, ...rest } = data;
//       setMeta(rest);
//       setPage(1);
//     } catch (err) {
//       const detail =
//         err?.response?.data?.error ||
//         err?.response?.data?.details ||
//         err?.message ||
//         "Failed to fetch from Copper.";
//       setError(typeof detail === "string" ? detail : JSON.stringify(detail));
//       setRawRows([]);
//       setMeta(null);
//     } finally {
//       setLoading(false);
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [config, filters, fromDate, toDate, refreshTick]);

//   useEffect(() => {
//     fetchData();
//   }, [fetchData]);

//   const renderCell = useCallback((col, row) => {
//     if (typeof col.render === "function") return col.render(row);
//     const v = row?.[col.key];
//     if (v === undefined || v === null || v === "") return "—";
//     if (typeof v === "object") return JSON.stringify(v);
//     return String(v);
//   }, []);

//   // Client-side text search across rendered string content.
//   const filteredRows = useMemo(() => {
//     if (!search.trim()) return rawRows;
//     const q = search.toLowerCase();
//     return rawRows.filter((row) =>
//       (config?.columns || []).some((col) => {
//         const text = String(renderCell(col, row) ?? "").toLowerCase();
//         return text.includes(q);
//       })
//     );
//   }, [rawRows, search, config, renderCell]);

//   const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
//   const pagedRows = useMemo(() => {
//     const start = (page - 1) * pageSize;
//     return filteredRows.slice(start, start + pageSize);
//   }, [filteredRows, page, pageSize]);

//   useEffect(() => {
//     if (page > totalPages) setPage(totalPages);
//   }, [page, totalPages]);

//   const updateFilter = (key, value) => {
//     setFilters((prev) => ({ ...prev, [key]: value }));
//   };

//   const clearFilters = () => {
//     setFilters(initialFilters);
//     setFromDate("");
//     setToDate("");
//     setSearch("");
//   };

//   const exportCsv = () => {
//     if (filteredRows.length === 0) {
//       NotificationManager.warning("Nothing to export.");
//       return;
//     }
//     const headers = config.columns.map((c) => c.label);
//     const lines = [headers.join(",")];
//     filteredRows.forEach((row) => {
//       const cells = config.columns.map((col) => {
//         const v = renderCell(col, row);
//         const str = String(v ?? "").replace(/"/g, '""');
//         return /[",\n]/.test(str) ? `"${str}"` : str;
//       });
//       lines.push(cells.join(","));
//     });
//     const blob = new Blob(["﻿" + lines.join("\n")], {
//       type: "text/csv;charset=utf-8;",
//     });
//     const url = URL.createObjectURL(blob);
//     const a = document.createElement("a");
//     a.href = url;
//     a.download = buildExportName("csv");
//     document.body.appendChild(a);
//     a.click();
//     document.body.removeChild(a);
//     URL.revokeObjectURL(url);
//     NotificationManager.success("CSV exported.");
//   };

//   const exportPdf = () => {
//     if (filteredRows.length === 0) {
//       NotificationManager.warning("Nothing to export.");
//       return;
//     }
//     const doc = new jsPDF("landscape");
//     const pageWidth = doc.internal.pageSize.width;
//     const margin = 14;
//     const today = fmtDate(Date.now());

//     try {
//       doc.addImage("/assets/images/BlueAsterAR.png", "PNG", pageWidth - 50, 10, 36, 12);
//     } catch (e) { /* logo optional */ }

//     doc.setFontSize(18);
//     doc.setTextColor(15, 23, 42);
//     doc.text(`Copper • ${config.title}`, margin, 18);

//     doc.setFontSize(10);
//     doc.setTextColor(71, 85, 105);
//     doc.text(`Generated on: ${today}`, margin, 26);
//     doc.text(`Records: ${filteredRows.length}`, margin, 32);

//     const filterDesc = describeFilters();
//     if (filterDesc) {
//       const split = doc.splitTextToSize(`Filters: ${filterDesc}`, pageWidth - margin * 2);
//       doc.text(split, margin, 38);
//     }

//     const headers = config.columns.map((c) => c.label);
//     const body = filteredRows.map((row) =>
//       config.columns.map((col) => String(renderCell(col, row) ?? ""))
//     );

//     autoTable(doc, {
//       head: [headers],
//       body,
//       startY: filterDesc ? 46 : 40,
//       styles: { fontSize: 8, cellPadding: 3 },
//       headStyles: { fillColor: [37, 99, 235], textColor: 255 },
//       alternateRowStyles: { fillColor: [240, 245, 255] },
//       margin: { left: margin, right: margin },
//     });

//     const totalPagesPdf = doc.internal.getNumberOfPages();
//     doc.setFontSize(8);
//     doc.setTextColor(120, 120, 120);
//     for (let i = 1; i <= totalPagesPdf; i++) {
//       doc.setPage(i);
//       const ph = doc.internal.pageSize.height;
//       doc.text(`Page ${i} of ${totalPagesPdf}`, pageWidth - margin - 25, ph - 6);
//     }

//     doc.save(buildExportName("pdf"));
//     NotificationManager.success("PDF exported.");
//   };

//   const describeFilters = () => {
//     const parts = [];
//     Object.entries(filters).forEach(([k, v]) => {
//       if (v !== "" && v !== undefined && v !== null) parts.push(`${k}=${v}`);
//     });
//     if (config.supportsDateRange) {
//       if (fromDate) parts.push(`from=${fromDate}`);
//       if (toDate) parts.push(`to=${toDate}`);
//     }
//     if (search) parts.push(`search="${search}"`);
//     return parts.join(" • ");
//   };

//   const buildExportName = (ext) => {
//     return `copper_${section}_${fmtFileDate()}.${ext}`;
//   };

//   if (!config) {
//     return (
//       <div className="cp-wrap">
//         <div className="cp-card cp-error-card">
//           <Icon icon="solar:danger-triangle-bold-duotone" />
//           <div>
//             <h6>Unknown section</h6>
//             <p>No Copper config defined for "{section}".</p>
//           </div>
//         </div>
//         {scopedStyles}
//       </div>
//     );
//   }

//   return (
//     <div className="cp-wrap">
//       <div className="cp-card">
//         {/* Header */}
//         <div className="cp-card-head">
//           <div className="cp-head-left">
//             <span className="cp-head-ico">
//               <Icon icon={config.icon} />
//             </span>
//             <div>
//               <h5>{config.title}</h5>
//               <p>{config.description}</p>
//             </div>
//           </div>
//           <div className="cp-head-right">
//             <span className="cp-count-pill">
//               <Icon icon="solar:database-bold-duotone" />
//               <strong>{filteredRows.length}</strong>
//               <span>of {rawRows.length}</span>
//             </span>
//             <button
//               type="button"
//               className="cp-btn cp-btn-ghost"
//               onClick={() => setRefreshTick((x) => x + 1)}
//               disabled={loading}
//               title="Refresh"
//             >
//               <Icon icon="solar:refresh-bold" />
//               Refresh
//             </button>
//             <button
//               type="button"
//               className="cp-btn cp-btn-ghost"
//               onClick={exportCsv}
//               disabled={loading}
//               title="Export CSV"
//             >
//               <Icon icon="solar:file-text-bold-duotone" />
//               CSV
//             </button>
//             <button
//               type="button"
//               className="cp-btn cp-btn-primary"
//               onClick={exportPdf}
//               disabled={loading}
//               title="Download PDF Report"
//             >
//               <Icon icon="solar:document-add-bold-duotone" />
//               PDF Report
//             </button>
//           </div>
//         </div>

//         {/* Filters */}
//         <div className="cp-filters">
//           {(config.filters || []).map((f) => (
//             <div className="cp-field" key={f.key}>
//               <label>{f.label}</label>
//               {f.type === "select" ? (
//                 <select
//                   value={filters[f.key] ?? ""}
//                   onChange={(e) => updateFilter(f.key, e.target.value)}
//                 >
//                   {f.options.map((opt) => (
//                     <option key={opt.value} value={opt.value}>
//                       {opt.label}
//                     </option>
//                   ))}
//                 </select>
//               ) : (
//                 <input
//                   type="text"
//                   value={filters[f.key] ?? ""}
//                   placeholder={f.placeholder || ""}
//                   onChange={(e) => updateFilter(f.key, e.target.value)}
//                 />
//               )}
//             </div>
//           ))}

//           {config.supportsDateRange && (
//             <>
//               <div className="cp-field">
//                 <label>From</label>
//                 <AppDatePicker
//                   value={fromDate}
//                   onChange={setFromDate}
//                   max={toDate || undefined}
//                   placeholder="From"
//                   ariaLabel="From date"
//                 />
//               </div>
//               <div className="cp-field">
//                 <label>To</label>
//                 <AppDatePicker
//                   value={toDate}
//                   onChange={setToDate}
//                   min={fromDate || undefined}
//                   placeholder="To"
//                   ariaLabel="To date"
//                 />
//               </div>
//             </>
//           )}

//           <div className="cp-field cp-field-grow">
//             <label>Search</label>
//             <input
//               type="text"
//               placeholder="Search visible rows..."
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//             />
//           </div>

//           <div className="cp-field cp-field-buttons">
//             <button
//               type="button"
//               className="cp-btn cp-btn-primary"
//               onClick={fetchData}
//               disabled={loading}
//             >
//               <Icon icon="solar:filter-bold-duotone" />
//               Apply
//             </button>
//             <button
//               type="button"
//               className="cp-btn cp-btn-ghost"
//               onClick={clearFilters}
//               disabled={loading}
//             >
//               <Icon icon="solar:close-circle-bold-duotone" />
//               Reset
//             </button>
//           </div>
//         </div>

//         {/* Body */}
//         {loading ? (
//           <div className="cp-loading">
//             <div className="cp-skel" style={{ width: "30%" }} />
//             <div className="cp-skel" />
//             <div className="cp-skel" />
//             <div className="cp-skel" />
//             <div className="cp-skel" />
//           </div>
//         ) : error ? (
//           <div className="cp-error-inline">
//             <Icon icon="solar:danger-triangle-bold-duotone" />
//             <div>
//               <strong>Failed to load.</strong>
//               <p>{error}</p>
//               <small>
//                 Make sure the copper-api server is running and{" "}
//                 <code>REACT_APP_COPPER_API</code> points at it (current: {COPPER_BASE}).
//               </small>
//             </div>
//             <button
//               className="cp-btn cp-btn-primary"
//               onClick={() => setRefreshTick((x) => x + 1)}
//             >
//               <Icon icon="solar:refresh-bold" />
//               Retry
//             </button>
//           </div>
//         ) : (
//           <>
//             <div className="cp-table-wrap">
//               <table className="cp-table">
//                 <thead>
//                   <tr>
//                     <th style={{ width: 60 }}>#</th>
//                     {config.columns.map((c) => (
//                       <th key={c.key}>{c.label}</th>
//                     ))}
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {pagedRows.map((row, idx) => (
//                     <tr key={(row.id || row.orderId || row.walletId || row.addressId || idx) + "-" + idx}>
//                       <td>
//                         <span className="cp-srno">
//                           {String((page - 1) * pageSize + idx + 1).padStart(2, "0")}
//                         </span>
//                       </td>
//                       {config.columns.map((c) => (
//                         <td key={c.key} className="cp-cell">
//                           {renderCell(c, row)}
//                         </td>
//                       ))}
//                     </tr>
//                   ))}
//                   {pagedRows.length === 0 && (
//                     <tr>
//                       <td colSpan={config.columns.length + 1}>
//                         <div className="cp-empty">
//                           <Icon icon="solar:inbox-bold-duotone" />
//                           <h6>No data</h6>
//                           <p>Adjust the filters or date range and try again.</p>
//                         </div>
//                       </td>
//                     </tr>
//                   )}
//                 </tbody>
//               </table>
//             </div>

//             {/* Footer / pagination */}
//             <div className="cp-foot">
//               <div className="cp-foot-left">
//                 <span>Show</span>
//                 <select
//                   value={pageSize}
//                   onChange={(e) => {
//                     setPageSize(Number(e.target.value));
//                     setPage(1);
//                   }}
//                 >
//                   {PAGE_SIZES.map((s) => (
//                     <option key={s} value={s}>
//                       {s}
//                     </option>
//                   ))}
//                 </select>
//                 <span>per page</span>
//                 {meta && meta.truncated ? (
//                   <span className="cp-trunc">
//                     <Icon icon="solar:info-circle-bold-duotone" />
//                     Server returned a truncated set ({meta.count} rows).
//                   </span>
//                 ) : null}
//               </div>
//               <div className="cp-foot-right">
//                 <button
//                   type="button"
//                   className="cp-pg-btn"
//                   onClick={() => setPage(1)}
//                   disabled={page === 1}
//                 >
//                   <Icon icon="solar:double-alt-arrow-left-bold" />
//                 </button>
//                 <button
//                   type="button"
//                   className="cp-pg-btn"
//                   onClick={() => setPage((p) => Math.max(1, p - 1))}
//                   disabled={page === 1}
//                 >
//                   <Icon icon="solar:alt-arrow-left-bold" />
//                 </button>
//                 <span className="cp-pg-info">
//                   Page <strong>{page}</strong> / {totalPages}
//                 </span>
//                 <button
//                   type="button"
//                   className="cp-pg-btn"
//                   onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
//                   disabled={page === totalPages}
//                 >
//                   <Icon icon="solar:alt-arrow-right-bold" />
//                 </button>
//                 <button
//                   type="button"
//                   className="cp-pg-btn"
//                   onClick={() => setPage(totalPages)}
//                   disabled={page === totalPages}
//                 >
//                   <Icon icon="solar:double-alt-arrow-right-bold" />
//                 </button>
//               </div>
//             </div>
//           </>
//         )}
//       </div>
//       {scopedStyles}
//     </div>
//   );
// };

// const scopedStyles = (
//   <style>{`
//     .cp-wrap { width: 100%; }
//     .cp-card {
//       background: #fff;
//       border-radius: 18px;
//       border: 1px solid #eef2f7;
//       box-shadow: 0 6px 24px rgba(15, 23, 42, 0.05);
//       overflow: hidden;
//     }

//     .cp-card-head {
//       display: flex; align-items: center; justify-content: space-between;
//       gap: 16px; flex-wrap: wrap;
//       padding: 22px 24px;
//       background:
//         radial-gradient(600px 200px at 0% 0%, rgba(37,99,235,0.06), transparent 60%),
//         linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
//       border-bottom: 1px solid #eef2f7;
//     }
//     .cp-head-left { display:flex; align-items:center; gap: 14px; min-width: 0; }
//     .cp-head-ico {
//       width: 46px; height: 46px; flex-shrink: 0;
//       display:inline-flex; align-items:center; justify-content:center;
//       border-radius: 12px;
//       background: linear-gradient(135deg, #2563eb, #0ea5e9);
//       color: #fff;
//       font-size: 24px;
//       box-shadow: 0 8px 18px rgba(37, 99, 235, 0.25);
//     }
//     .cp-head-left h5 { margin: 0; font-size: 18px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; }
//     .cp-head-left p { margin: 2px 0 0; font-size: 13px; color: #64748b; max-width: 560px; }
//     .cp-head-right { display:flex; align-items:center; gap:10px; flex-wrap: wrap; }
//     .cp-count-pill {
//       display:inline-flex; align-items:center; gap: 8px;
//       padding: 8px 14px;
//       background: #eff6ff;
//       color: #1d4ed8;
//       border-radius: 999px;
//       font-size: 13px;
//       border: 1px solid #dbeafe;
//     }
//     .cp-count-pill strong { font-size: 15px; font-weight: 700; }
//     .cp-count-pill svg { font-size: 16px; }

//     .cp-filters {
//       display: grid;
//       grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
//       gap: 12px;
//       padding: 18px 24px;
//       border-bottom: 1px solid #eef2f7;
//       background: #fcfdff;
//     }
//     .cp-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
//     .cp-field-grow { grid-column: span 2; }
//     .cp-field-buttons {
//       flex-direction: row;
//       align-items: flex-end;
//       gap: 8px;
//       grid-column: span 1;
//     }
//     .cp-field label {
//       font-size: 11px;
//       font-weight: 600;
//       text-transform: uppercase;
//       letter-spacing: 0.4px;
//       color: #475569;
//     }
//     .cp-field input,
//     .cp-field select {
//       height: 40px;
//       padding: 0 12px;
//       border: 1.5px solid #e2e8f0;
//       border-radius: 10px;
//       background: #fff;
//       color: #0f172a;
//       outline: none;
//       font-size: 13.5px;
//       transition: all .2s ease;
//       width: 100%;
//     }
//     .cp-field input:focus,
//     .cp-field select:focus {
//       border-color: #2563eb;
//       box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
//     }

//     .cp-table-wrap { padding: 8px 16px 4px; overflow-x: auto; }
//     .cp-table {
//       width: 100%;
//       border-collapse: separate;
//       border-spacing: 0;
//       font-size: 13.5px;
//     }
//     .cp-table thead th {
//       background: #f8fafc;
//       color: #475569;
//       font-size: 11.5px;
//       font-weight: 600;
//       text-transform: uppercase;
//       letter-spacing: 0.4px;
//       padding: 12px 14px;
//       text-align: left;
//       border-bottom: 1px solid #eef2f7;
//       white-space: nowrap;
//     }
//     .cp-table tbody td {
//       padding: 12px 14px;
//       vertical-align: middle;
//       color: #1e293b;
//       border-bottom: 1px solid #f1f5f9;
//       background: #fff;
//       transition: background .15s ease;
//       max-width: 280px;
//       overflow: hidden;
//       text-overflow: ellipsis;
//       white-space: nowrap;
//     }
//     .cp-table tbody tr:hover td { background: #f8fbff; }
//     .cp-srno {
//       display:inline-flex; align-items:center; justify-content:center;
//       min-width: 32px; height: 28px;
//       padding: 0 8px;
//       border-radius: 8px;
//       background: #f1f5f9;
//       color: #475569;
//       font-size: 12px;
//       font-weight: 600;
//     }

//     .cp-foot {
//       display:flex; align-items:center; justify-content:space-between;
//       padding: 14px 24px 18px;
//       gap: 12px;
//       flex-wrap: wrap;
//       border-top: 1px solid #eef2f7;
//       background: #fcfdff;
//     }
//     .cp-foot-left { display:flex; align-items:center; gap: 8px; color: #475569; font-size: 13px; flex-wrap: wrap; }
//     .cp-foot-left select {
//       height: 32px; padding: 0 10px;
//       border: 1.5px solid #e2e8f0; border-radius: 8px; background: #fff;
//     }
//     .cp-trunc {
//       display:inline-flex; align-items:center; gap:6px;
//       margin-left: 12px;
//       color: #b45309;
//       font-size: 12.5px;
//     }
//     .cp-foot-right { display:flex; align-items:center; gap: 6px; }
//     .cp-pg-btn {
//       width: 34px; height: 34px;
//       display:inline-flex; align-items:center; justify-content:center;
//       background: #fff;
//       border: 1.5px solid #e2e8f0;
//       border-radius: 8px;
//       cursor: pointer;
//       color: #334155;
//       transition: all .15s ease;
//     }
//     .cp-pg-btn:hover:not(:disabled) {
//       background: #eff6ff;
//       border-color: #bfdbfe;
//       color: #1d4ed8;
//     }
//     .cp-pg-btn:disabled { opacity: .45; cursor: not-allowed; }
//     .cp-pg-info { font-size: 13px; color: #475569; padding: 0 8px; }

//     .cp-btn {
//       display:inline-flex; align-items:center; gap: 6px;
//       padding: 8px 14px;
//       border: none;
//       border-radius: 10px;
//       font-size: 13px;
//       font-weight: 600;
//       cursor: pointer;
//       transition: all .2s ease;
//       white-space: nowrap;
//     }
//     .cp-btn:disabled { opacity: .6; cursor: not-allowed; }
//     .cp-btn svg { font-size: 16px; }
//     .cp-btn-primary {
//       color: #fff;
//       background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 60%, #0ea5e9 100%);
//       background-size: 200% 200%;
//       background-position: 0% 50%;
//       box-shadow: 0 8px 16px rgba(37, 99, 235, 0.25);
//     }
//     .cp-btn-primary:hover:not(:disabled) {
//       background-position: 100% 50%;
//       transform: translateY(-1px);
//       box-shadow: 0 12px 22px rgba(37, 99, 235, 0.32);
//     }
//     .cp-btn-ghost {
//       color: #1d4ed8;
//       background: #fff;
//       border: 1.5px solid #dbeafe;
//     }
//     .cp-btn-ghost:hover:not(:disabled) {
//       background: #eff6ff;
//       border-color: #93c5fd;
//       color: #1e3a8a;
//     }

//     .cp-empty, .cp-loading, .cp-error-inline {
//       padding: 48px 16px;
//       text-align: center;
//       color: #64748b;
//     }
//     .cp-empty { display:flex; flex-direction:column; align-items:center; gap: 6px; }
//     .cp-empty svg { font-size: 56px; color: #cbd5e1; }
//     .cp-empty h6 { margin: 6px 0 0; font-size: 16px; color: #334155; font-weight: 600; }
//     .cp-empty p { margin: 0; font-size: 13px; }

//     .cp-loading { padding: 22px 24px; text-align: left; }
//     .cp-skel {
//       height: 24px;
//       margin-bottom: 12px;
//       background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
//       background-size: 200% 100%;
//       animation: cp-shimmer 1.4s linear infinite;
//       border-radius: 8px;
//     }
//     @keyframes cp-shimmer {
//       0% { background-position: 200% 0; }
//       100% { background-position: -200% 0; }
//     }

//     .cp-error-inline {
//       display:flex; align-items: center; justify-content: center; gap: 14px; flex-wrap: wrap;
//       padding: 28px 22px;
//     }
//     .cp-error-inline svg { font-size: 36px; color: #ef4444; }
//     .cp-error-inline > div { text-align: left; max-width: 560px; }
//     .cp-error-inline strong { font-size: 14.5px; color: #0f172a; }
//     .cp-error-inline p { margin: 4px 0 4px; font-size: 13px; color: #64748b; word-break: break-word; }
//     .cp-error-inline small { font-size: 12px; color: #94a3b8; }
//     .cp-error-inline code {
//       background: #f1f5f9; padding: 1px 6px; border-radius: 4px;
//       font-size: 11.5px; color: #1d4ed8;
//     }

//     .cp-error-card {
//       display:flex; align-items:center; gap: 14px; padding: 22px 24px; flex-wrap: wrap;
//     }
//     .cp-error-card > svg { font-size: 36px; color: #ef4444; }
//     .cp-error-card h6 { margin: 0; font-size: 15px; color: #0f172a; font-weight: 600; }
//     .cp-error-card p { margin: 2px 0 0; font-size: 13px; color: #64748b; }

//     @media (max-width: 768px) {
//       .cp-card-head { padding: 18px; }
//       .cp-head-left h5 { font-size: 16px; }
//       .cp-filters { padding: 14px 16px; }
//       .cp-field-grow { grid-column: span 1; }
//     }
//   `}</style>
// );

// export default CopperLayer;


import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { NotificationManager } from "react-notifications";
import { COPPER_SECTIONS, fmtDate, fmtFileDate, formatAmount } from "./copperConfig";
import { copperApi, COPPER_BASE } from "./copperApi";
import { useTimezone } from "../../hook/useTimezone";
import AppDatePicker from "../AppDatePicker";

const PAGE_SIZES = [10, 25, 50, 100];

// Helper function to format price
const formatPrice = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (isNaN(num) || num === 0) return "—";
  
  // For small values (like XRP), show more decimals
  if (num < 1 && num > 0) {
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;
  }
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Mapping for API price IDs
const getPriceId = (currency) => {
  const mapping = {
    // Major cryptocurrencies
    'BTC': 'bitcoin',
    'ETH': 'ethereum',
    'USDT': 'tether',
    'USDC': 'usd-coin',
    'BNB': 'binancecoin',
    'XRP': 'ripple',
    'SOL': 'solana',
    'ADA': 'cardano',
    'DOGE': 'dogecoin',
    'DOT': 'polkadot',
    'MATIC': 'matic-network',
    'SHIB': 'shiba-inu',
    'TRX': 'tron',
    'AVAX': 'avalanche-2',
    'LINK': 'chainlink',
    'UNI': 'uniswap',
    'ATOM': 'cosmos',
    'ETC': 'ethereum-classic',
    'XTZ': 'tezos',
    'ALGO': 'algorand',
    'VET': 'vechain',
    'THETA': 'theta-token',
    'FTM': 'fantom',
    'EGLD': 'elrond-erd-2',
    'EOS': 'eos',
    'NEO': 'neo',
    'DASH': 'dash',
    'ZEC': 'zcash',
    'BCH': 'bitcoin-cash',
    'LTC': 'litecoin',
    'DAI': 'dai',
    'BUSD': 'binance-usd',
    'XAUT': 'tether-gold',
    'PAXG': 'pax-gold',
    'AAVE': 'aave',
    'MKR': 'maker',
    'COMP': 'compound-governance-token',
    'YFI': 'yearn-finance',
    'ARB': 'arbitrum',
    'OP': 'optimism',
    'APT': 'aptos',
    'SUI': 'sui'
  };
  
  return mapping[currency] || null;
};

// Real-time price fetching service
class PriceService {
  static cache = new Map();
  static cacheExpiry = 60000; // 1 minute cache
  
  static getFallbackPrice(currency) {
    // Realistic fallback prices based on actual market data
    const fallbacks = {
      'XRP': 0.5123,
      'XAUT': 4534.92,
      'BTC': 43250.00,
      'ETH': 2250.00,
      'USDT': 0.9999,
      'USDC': 1.00,
      'SOL': 85.50,
      'BCH': 225.00,
      'LTC': 68.50,
      'DOGE': 0.082,
      'ADA': 0.35,
      'DOT': 6.80,
      'MATIC': 0.75,
      'LINK': 14.20,
      'UNI': 5.80,
      'ATOM': 9.50,
      'ETC': 22.00,
      'XTZ': 0.95,
      'ALGO': 0.18,
      'VET': 0.023,
      'AVAX': 32.00,
      'FTM': 0.42,
      'EGLD': 48.00,
      'EOS': 0.72,
      'NEO': 11.50,
      'DASH': 28.00,
      'ZEC': 24.00,
      'TRX': 0.11,
      'SHIB': 0.0000085,
      'BNB': 310.00,
      'DAI': 1.00,
      'BUSD': 1.00,
      'PAXG': 2340.00,
      'AAVE': 85.00,
      'MKR': 1650.00,
      'COMP': 42.00,
      'YFI': 6200.00
    };
    return fallbacks[currency] || 0;
  }
  
  static async fetchPrices(currencies) {
    const now = Date.now();
    const uniqueCurrencies = [...new Set(currencies)];
    const results = {};
    const toFetch = [];
    
    // Check cache first
    for (const currency of uniqueCurrencies) {
      const cached = this.cache.get(currency);
      if (cached && (now - cached.timestamp) < this.cacheExpiry) {
        results[currency] = cached.price;
      } else {
        toFetch.push(currency);
      }
    }
    
    if (toFetch.length === 0) {
      return results;
    }
    
    // Try multiple price APIs
    let prices = {};
    
    // Try CoinGecko first
    prices = await this.fetchFromCoinGecko(toFetch);
    
    // If CoinGecko fails or returns incomplete data, try Binance
    if (Object.keys(prices).length < toFetch.length) {
      const binancePrices = await this.fetchFromBinance(toFetch);
      prices = { ...prices, ...binancePrices };
    }
    
    // If still missing prices, use fallbacks
    for (const currency of toFetch) {
      if (!prices[currency] || prices[currency] === 0) {
        prices[currency] = this.getFallbackPrice(currency);
        console.log(`Using fallback price for ${currency}: ${prices[currency]}`);
      }
    }
    
    // Cache the results
    for (const [currency, price] of Object.entries(prices)) {
      results[currency] = price;
      this.cache.set(currency, { price, timestamp: now });
    }
    
    return results;
  }
  
  static async fetchFromCoinGecko(currencies) {
    try {
      const priceIds = currencies.map(c => getPriceId(c)).filter(id => id !== null);
      if (priceIds.length === 0) return {};
      
      const ids = priceIds.join(',');
      const response = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd`,
        {
          headers: {
            'Accept': 'application/json',
          }
        }
      );
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      
      const prices = {};
      for (const currency of currencies) {
        const priceId = getPriceId(currency);
        if (priceId && data[priceId] && data[priceId].usd) {
          prices[currency] = data[priceId].usd;
        }
      }
      
      return prices;
    } catch (error) {
      console.error('CoinGecko API error:', error);
      return {};
    }
  }
  
  static async fetchFromBinance(currencies) {
    try {
      const prices = {};
      
      // Handle each currency individually to avoid rate limits
      for (const currency of currencies) {
        let symbol = '';
        
        // Map currency to Binance symbol
        switch(currency) {
          case 'BTC': symbol = 'BTCUSDT'; break;
          case 'ETH': symbol = 'ETHUSDT'; break;
          case 'XRP': symbol = 'XRPUSDT'; break;
          case 'XAUT': symbol = 'XAUTUSDT'; break;
          case 'SOL': symbol = 'SOLUSDT'; break;
          case 'BCH': symbol = 'BCHUSDT'; break;
          case 'LTC': symbol = 'LTCUSDT'; break;
          case 'ADA': symbol = 'ADAUSDT'; break;
          case 'DOT': symbol = 'DOTUSDT'; break;
          case 'MATIC': symbol = 'MATICUSDT'; break;
          case 'LINK': symbol = 'LINKUSDT'; break;
          case 'UNI': symbol = 'UNIUSDT'; break;
          case 'AVAX': symbol = 'AVAXUSDT'; break;
          case 'ATOM': symbol = 'ATOMUSDT'; break;
          case 'ETC': symbol = 'ETCUSDT'; break;
          case 'XTZ': symbol = 'XTZUSDT'; break;
          case 'ALGO': symbol = 'ALGOUSDT'; break;
          case 'VET': symbol = 'VETUSDT'; break;
          case 'FTM': symbol = 'FTMUSDT'; break;
          case 'EOS': symbol = 'EOSUSDT'; break;
          case 'NEO': symbol = 'NEOUSDT'; break;
          case 'DASH': symbol = 'DASHUSDT'; break;
          case 'ZEC': symbol = 'ZECUSDT'; break;
          case 'TRX': symbol = 'TRXUSDT'; break;
          case 'DOGE': symbol = 'DOGEUSDT'; break;
          case 'BNB': symbol = 'BNBUSDT'; break;
          default: continue;
        }
        
        try {
          const response = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol}`);
          if (response.ok) {
            const data = await response.json();
            if (data.price) {
              prices[currency] = parseFloat(data.price);
            }
          }
          // Add small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {
          console.warn(`Failed to fetch ${currency} from Binance:`, err);
        }
      }
      
      return prices;
    } catch (error) {
      console.error('Binance API error:', error);
      return {};
    }
  }
  
  static async fetchBatch(currencies, onProgress) {
    const prices = await this.fetchPrices(currencies);
    if (onProgress) onProgress(100);
    return prices;
  }
}

const CopperLayer = ({ section }) => {
  const config = COPPER_SECTIONS[section];
  useTimezone();

  const [rawRows, setRawRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [marketPrices, setMarketPrices] = useState({});
  const [priceLoading, setPriceLoading] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  const priceRefreshInterval = useRef(null);

  const initialFilters = useMemo(() => {
    const out = {};
    (config?.filters || []).forEach((f) => {
      out[f.key] = f.defaultValue ?? "";
    });
    return out;
  }, [config]);

  const [filters, setFilters] = useState(initialFilters);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  // Reset state when switching sections
  useEffect(() => {
    setFilters(initialFilters);
    setFromDate("");
    setToDate("");
    setSearch("");
    setPage(1);
    setMarketPrices({});
    
    // Clear price refresh interval
    if (priceRefreshInterval.current) {
      clearInterval(priceRefreshInterval.current);
      priceRefreshInterval.current = null;
    }
    
    return () => {
      if (priceRefreshInterval.current) {
        clearInterval(priceRefreshInterval.current);
      }
    };
  }, [section, initialFilters]);

  // Fetch market prices
  const fetchMarketPrices = useCallback(async (currencies, showNotification = false) => {
    if (!currencies || currencies.length === 0) return;
    
    setPriceLoading(true);
    try {
      const prices = await PriceService.fetchBatch(currencies);
      
      setMarketPrices(prev => {
        const newPrices = { ...prev, ...prices };
        console.log('Updated market prices:', newPrices); // Debug log
        return newPrices;
      });
      setLastPriceUpdate(new Date());
      
      if (showNotification) {
        const updatedCount = Object.keys(prices).length;
        // NotificationManager.success(`Updated prices for ${updatedCount} currencies`);
      }
    } catch (error) {
      console.error('Error fetching market prices:', error);
      if (showNotification) {
        NotificationManager.error("Failed to fetch market prices");
      }
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // Auto-refresh prices every minute
  useEffect(() => {
    if (section === 'wallets' && rawRows.length > 0) {
      if (priceRefreshInterval.current) {
        clearInterval(priceRefreshInterval.current);
      }
      
      priceRefreshInterval.current = setInterval(() => {
        const currencies = [...new Set(rawRows.map(row => row.currency))];
        fetchMarketPrices(currencies, false);
      }, 60000); // Refresh every minute
      
      return () => {
        if (priceRefreshInterval.current) {
          clearInterval(priceRefreshInterval.current);
        }
      };
    }
  }, [section, rawRows, fetchMarketPrices]);

  // const fetchData = useCallback(async () => {
  //   if (!config) return;
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const params = {};
  //     Object.entries(filters).forEach(([k, v]) => {
  //       if (v !== "" && v !== undefined && v !== null) params[k] = v;
  //     });
  //     if (config.supportsDateRange) {
  //       if (fromDate) params.fromDate = fromDate;
  //       if (toDate) params.toDate = toDate;
  //     }
  //     const res = await copperApi.get(config.endpoint, { params });
  //     const data = res.data || {};
  //     const rows = Array.isArray(data[config.listKey]) ? data[config.listKey] : [];
  //     setRawRows(rows);
  //     const { [config.listKey]: _omit, ...rest } = data;
  //     setMeta(rest);
  //     setPage(1);
      
  //     // Fetch market prices for wallet section
  //     if (section === 'wallets' && rows.length > 0) {
  //       const currencies = [...new Set(rows.map(row => row.currency))];
  //       console.log('Fetching prices for currencies:', currencies); // Debug log
  //       await fetchMarketPrices(currencies, true);
  //     }
  //   } catch (err) {
  //     const detail =
  //       err?.response?.data?.error ||
  //       err?.response?.data?.details ||
  //       err?.message ||
  //       "Failed to fetch from Copper.";
  //     setError(typeof detail === "string" ? detail : JSON.stringify(detail));
  //     setRawRows([]);
  //     setMeta(null);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [config, filters, fromDate, toDate, refreshTick, section, fetchMarketPrices]);
  // In CopperLayer.jsx, update the fetchData function:

const fetchData = useCallback(async () => {
  if (!config) return;
  setLoading(true);
  setError(null);
  try {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== undefined && v !== null) params[k] = v;
    });
    
    // Add minBalance filter
    if (section === 'wallets' && filters.minBalance && filters.minBalance !== "0") {
      params.minBalance = filters.minBalance;
    }
    
    if (config.supportsDateRange) {
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
    }
    const res = await copperApi.get(config.endpoint, { params });
    let data = res.data || {};
    
    // Apply post-processing if defined
    if (config.postProcess) {
      data = config.postProcess(data);
      
      // Show notification about filtered rows
      if (data.originalCount && data.filteredCount && data.originalCount !== data.filteredCount) {
        const hiddenCount = data.originalCount - data.filteredCount;
        // NotificationManager.info(`Showing ${data.filteredCount} of ${data.originalCount} wallets (${hiddenCount} hidden with balance < ${filters.minBalance || 10})`);
      }
    }
    
    const rows = Array.isArray(data[config.listKey]) ? data[config.listKey] : [];
    setRawRows(rows);
    const { [config.listKey]: _omit, ...rest } = data;
    setMeta(rest);
    setPage(1);
    
    // Fetch market prices for wallet section
    if (section === 'wallets' && rows.length > 0) {
      const currencies = [...new Set(rows.map(row => row.currency))];
      console.log('Fetching prices for currencies:', currencies);
      await fetchMarketPrices(currencies, true);
    }
  } catch (err) {
    const detail =
      err?.response?.data?.error ||
      err?.response?.data?.details ||
      err?.message ||
      "Failed to fetch from Copper.";
    setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    setRawRows([]);
    setMeta(null);
  } finally {
    setLoading(false);
  }
}, [config, filters, fromDate, toDate, refreshTick, section, fetchMarketPrices]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate total portfolio value for allocations
  const totalPortfolioValue = useMemo(() => {
    if (section !== 'wallets' || rawRows.length === 0) return 0;
    
    return rawRows.reduce((sum, row) => {
      const balance = parseFloat(row.totalBalance || row.balance || 0);
      const price = marketPrices[row.currency] || PriceService.getFallbackPrice(row.currency);
      return sum + (balance * price);
    }, 0);
  }, [rawRows, marketPrices, section]);

  const renderCell = useCallback((col, row) => {
    // Handle market price column
    if (col.key === "marketPrice") {
      let price = marketPrices[row.currency];
      
      // If price not in marketPrices, try to get fallback
      if (!price || price === 0) {
        price = PriceService.getFallbackPrice(row.currency);
      }
      
      if (priceLoading && !marketPrices[row.currency]) {
        return <span className="price-loading">Loading...</span>;
      }
      
      const formattedPrice = formatPrice(price);
      console.log(`Price for ${row.currency}: ${price} -> ${formattedPrice}`); // Debug log
      return formattedPrice;
    }
    
    // Handle allocation column
    if (col.key === "allocation") {
      if (totalPortfolioValue === 0) return "0.00%";
      const balance = parseFloat(row.totalBalance || row.balance || 0);
      let price = marketPrices[row.currency] || PriceService.getFallbackPrice(row.currency);
      const value = balance * price;
      const allocation = (value / totalPortfolioValue) * 100;
      return `${allocation.toFixed(2)}%`;
    }
    
    // Use custom render function if provided
    if (typeof col.render === "function") return col.render(row);
    
    // Default rendering
    const v = row?.[col.key];
    if (v === undefined || v === null || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  }, [marketPrices, totalPortfolioValue, priceLoading]);

  // Manual price refresh handler
  const handleRefreshPrices = useCallback(async () => {
    if (section === 'wallets' && rawRows.length > 0) {
      const currencies = [...new Set(rawRows.map(row => row.currency))];
      await fetchMarketPrices(currencies, true);
    }
  }, [section, rawRows, fetchMarketPrices]);

  // Client-side text search across rendered string content
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rawRows;
    const q = search.toLowerCase();
    return rawRows.filter((row) =>
      (config?.columns || []).some((col) => {
        const text = String(renderCell(col, row) ?? "").toLowerCase();
        return text.includes(q);
      })
    );
  }, [rawRows, search, config, renderCell]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setFromDate("");
    setToDate("");
    setSearch("");
  };

  const exportCsv = () => {
    if (filteredRows.length === 0) {
      NotificationManager.warning("Nothing to export.");
      return;
    }
    const headers = config.columns.map((c) => c.label);
    const lines = [headers.join(",")];
    filteredRows.forEach((row) => {
      const cells = config.columns.map((col) => {
        const v = renderCell(col, row);
        const str = String(v ?? "").replace(/"/g, '""');
        return /[",\n]/.test(str) ? `"${str}"` : str;
      });
      lines.push(cells.join(","));
    });
    const blob = new Blob(["﻿" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = buildExportName("csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    NotificationManager.success("CSV exported.");
  };

  const exportPdf = () => {
    if (filteredRows.length === 0) {
      NotificationManager.warning("Nothing to export.");
      return;
    }
    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const today = fmtDate(Date.now());

    try {
      doc.addImage("/assets/images/BlueAsterAR.png", "PNG", pageWidth - 50, 10, 36, 12);
    } catch (e) { /* logo optional */ }

    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text(`Copper • ${config.title}`, margin, 18);

    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text(`Generated on: ${today}`, margin, 26);
    doc.text(`Records: ${filteredRows.length}`, margin, 32);

    const filterDesc = describeFilters();
    if (filterDesc) {
      const split = doc.splitTextToSize(`Filters: ${filterDesc}`, pageWidth - margin * 2);
      doc.text(split, margin, 38);
    }

    const headers = config.columns.map((c) => c.label);
    const body = filteredRows.map((row) =>
      config.columns.map((col) => String(renderCell(col, row) ?? ""))
    );

    autoTable(doc, {
      head: [headers],
      body,
      startY: filterDesc ? 46 : 40,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      alternateRowStyles: { fillColor: [240, 245, 255] },
      margin: { left: margin, right: margin },
    });

    const totalPagesPdf = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    for (let i = 1; i <= totalPagesPdf; i++) {
      doc.setPage(i);
      const ph = doc.internal.pageSize.height;
      doc.text(`Page ${i} of ${totalPagesPdf}`, pageWidth - margin - 25, ph - 6);
    }

    doc.save(buildExportName("pdf"));
    NotificationManager.success("PDF exported.");
  };

  const describeFilters = () => {
    const parts = [];
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== undefined && v !== null) parts.push(`${k}=${v}`);
    });
    if (config.supportsDateRange) {
      if (fromDate) parts.push(`from=${fromDate}`);
      if (toDate) parts.push(`to=${toDate}`);
    }
    if (search) parts.push(`search="${search}"`);
    return parts.join(" • ");
  };

  const buildExportName = (ext) => {
    return `copper_${section}_${fmtFileDate()}.${ext}`;
  };

  if (!config) {
    return (
      <div className="cp-wrap">
        <div className="cp-card cp-error-card">
          <Icon icon="solar:danger-triangle-bold-duotone" />
          <div>
            <h6>Unknown section</h6>
            <p>No Copper config defined for "{section}".</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-wrap">
      <div className="cp-card">
        {/* Header */}
        <div className="cp-card-head">
          <div className="cp-head-left">
            <span className="cp-head-ico">
              <Icon icon={config.icon} />
            </span>
            <div>
              <h5>{config.title}</h5>
              <p>{config.description}</p>
            </div>
          </div>
          <div className="cp-head-right">
            <span className="cp-count-pill">
              <Icon icon="solar:database-bold-duotone" />
              <strong>{filteredRows.length}</strong>
              <span>of {rawRows.length}</span>
            </span>
            {section === 'wallets' && (
              <button
                type="button"
                className="cp-btn cp-btn-ghost"
                onClick={handleRefreshPrices}
                disabled={priceLoading}
                title="Refresh market prices"
              >
                <Icon icon="solar:chart-2-bold-duotone" />
                {priceLoading ? "Loading..." : "Refresh Prices"}
              </button>
            )}
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={() => setRefreshTick((x) => x + 1)}
              disabled={loading}
              title="Refresh"
            >
              <Icon icon="solar:refresh-bold" />
              Refresh
            </button>
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={exportCsv}
              disabled={loading}
              title="Export CSV"
            >
              <Icon icon="solar:file-text-bold-duotone" />
              CSV
            </button>
            <button
              type="button"
              className="cp-btn cp-btn-primary"
              onClick={exportPdf}
              disabled={loading}
              title="Download PDF Report"
            >
              <Icon icon="solar:document-add-bold-duotone" />
              PDF Report
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="cp-filters">
          {(config.filters || []).map((f) => (
            <div className="cp-field" key={f.key}>
              <label>{f.label}</label>
              {f.type === "select" ? (
                <select
                  value={filters[f.key] ?? ""}
                  onChange={(e) => updateFilter(f.key, e.target.value)}
                >
                  {f.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={filters[f.key] ?? ""}
                  placeholder={f.placeholder || ""}
                  onChange={(e) => updateFilter(f.key, e.target.value)}
                />
              )}
            </div>
          ))}

          {config.supportsDateRange && (
            <>
              <div className="cp-field">
                <label>From</label>
                <AppDatePicker
                  value={fromDate}
                  onChange={setFromDate}
                  max={toDate || undefined}
                  placeholder="From"
                  ariaLabel="From date"
                />
              </div>
              <div className="cp-field">
                <label>To</label>
                <AppDatePicker
                  value={toDate}
                  onChange={setToDate}
                  min={fromDate || undefined}
                  placeholder="To"
                  ariaLabel="To date"
                />
              </div>
            </>
          )}

          <div className="cp-field cp-field-grow">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search visible rows..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="cp-field cp-field-buttons">
            <button
              type="button"
              className="cp-btn cp-btn-primary"
              onClick={fetchData}
              disabled={loading}
            >
              <Icon icon="solar:filter-bold-duotone" />
              Apply
            </button>
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={clearFilters}
              disabled={loading}
            >
              <Icon icon="solar:close-circle-bold-duotone" />
              Reset
            </button>
          </div>
        </div>

        {/* Last price update indicator */}
        {section === 'wallets' && lastPriceUpdate && (
          <div className="cp-price-info">
            <Icon icon="solar:clock-circle-bold-duotone" />
            <span>Prices updated: {lastPriceUpdate.toLocaleTimeString()}</span>
            <span className="cp-auto-refresh">Auto-refreshes every minute</span>
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="cp-loading">
            <div className="cp-skel" style={{ width: "30%" }} />
            <div className="cp-skel" />
            <div className="cp-skel" />
            <div className="cp-skel" />
            <div className="cp-skel" />
          </div>
        ) : error ? (
          <div className="cp-error-inline">
            <Icon icon="solar:danger-triangle-bold-duotone" />
            <div>
              <strong>Failed to load.</strong>
              <p>{error}</p>
              <small>
                Make sure the copper-api server is running and{" "}
                <code>REACT_APP_COPPER_API</code> points at it (current: {COPPER_BASE}).
              </small>
            </div>
            <button
              className="cp-btn cp-btn-primary"
              onClick={() => setRefreshTick((x) => x + 1)}
            >
              <Icon icon="solar:refresh-bold" />
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="cp-table-wrap">
              <table className="cp-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    {config.columns.map((c) => (
                      <th key={c.key}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row, idx) => (
                    <tr key={(row.id || row.orderId || row.walletId || row.addressId || idx) + "-" + idx}>
                      <td>
                        <span className="cp-srno">
                          {String((page - 1) * pageSize + idx + 1).padStart(2, "0")}
                        </span>
                      </td>
                      {config.columns.map((c) => (
                        <td key={c.key} className="cp-cell">
                          {renderCell(c, row)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {pagedRows.length === 0 && (
                    <tr>
                      <td colSpan={config.columns.length + 1}>
                        <div className="cp-empty">
                          <Icon icon="solar:inbox-bold-duotone" />
                          <h6>No data</h6>
                          <p>Adjust the filters or date range and try again.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer / pagination */}
            <div className="cp-foot">
              <div className="cp-foot-left">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <span>per page</span>
                {meta && meta.truncated ? (
                  <span className="cp-trunc">
                    <Icon icon="solar:info-circle-bold-duotone" />
                    Server returned a truncated set ({meta.count} rows).
                  </span>
                ) : null}
              </div>
              <div className="cp-foot-right">
                <button
                  type="button"
                  className="cp-pg-btn"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  <Icon icon="solar:double-alt-arrow-left-bold" />
                </button>
                <button
                  type="button"
                  className="cp-pg-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <Icon icon="solar:alt-arrow-left-bold" />
                </button>
                <span className="cp-pg-info">
                  Page <strong>{page}</strong> / {totalPages}
                </span>
                <button
                  type="button"
                  className="cp-pg-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <Icon icon="solar:alt-arrow-right-bold" />
                </button>
                <button
                  type="button"
                  className="cp-pg-btn"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                >
                  <Icon icon="solar:double-alt-arrow-right-bold" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      <style>{`
        .cp-wrap { width: 100%; }
        .cp-card {
          background: #fff;
          border-radius: 18px;
          border: 1px solid #eef2f7;
          box-shadow: 0 6px 24px rgba(15, 23, 42, 0.05);
          overflow: hidden;
        }

        .cp-card-head {
          display: flex; align-items: center; justify-content: space-between;
          gap: 16px; flex-wrap: wrap;
          padding: 22px 24px;
          background:
            radial-gradient(600px 200px at 0% 0%, rgba(37,99,235,0.06), transparent 60%),
            linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
          border-bottom: 1px solid #eef2f7;
        }
        
        .cp-price-info {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 24px;
          background: #f0fdf4;
          border-bottom: 1px solid #dcfce7;
          font-size: 12px;
          color: #166534;
        }
        
        .cp-price-info svg {
          font-size: 14px;
        }
        
        .cp-auto-refresh {
          margin-left: auto;
          font-size: 11px;
          color: #15803d;
        }
        
        .price-loading {
          color: #64748b;
          font-style: italic;
          font-size: 12px;
        }
        
        .cp-head-left { display:flex; align-items:center; gap: 14px; min-width: 0; }
        .cp-head-ico {
          width: 46px; height: 46px; flex-shrink: 0;
          display:inline-flex; align-items:center; justify-content:center;
          border-radius: 12px;
          background: linear-gradient(135deg, #2563eb, #0ea5e9);
          color: #fff;
          font-size: 24px;
          box-shadow: 0 8px 18px rgba(37, 99, 235, 0.25);
        }
        .cp-head-left h5 { margin: 0; font-size: 18px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; }
        .cp-head-left p { margin: 2px 0 0; font-size: 13px; color: #64748b; max-width: 560px; }
        .cp-head-right { display:flex; align-items:center; gap:10px; flex-wrap: wrap; }
        .cp-count-pill {
          display:inline-flex; align-items:center; gap: 8px;
          padding: 8px 14px;
          background: #eff6ff;
          color: #1d4ed8;
          border-radius: 999px;
          font-size: 13px;
          border: 1px solid #dbeafe;
        }
        .cp-count-pill strong { font-size: 15px; font-weight: 700; }
        .cp-count-pill svg { font-size: 16px; }

        .cp-filters {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
          padding: 18px 24px;
          border-bottom: 1px solid #eef2f7;
          background: #fcfdff;
        }
        .cp-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
        .cp-field-grow { grid-column: span 2; }
        .cp-field-buttons {
          flex-direction: row;
          align-items: flex-end;
          gap: 8px;
          grid-column: span 1;
        }
        .cp-field label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          color: #475569;
        }
        .cp-field input,
        .cp-field select {
          height: 40px;
          padding: 0 12px;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          background: #fff;
          color: #0f172a;
          outline: none;
          font-size: 13.5px;
          transition: all .2s ease;
          width: 100%;
        }
        .cp-field input:focus,
        .cp-field select:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
        }

        .cp-table-wrap { padding: 8px 16px 4px; overflow-x: auto; }
        .cp-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 13.5px;
        }
        .cp-table thead th {
          background: #f8fafc;
          color: #475569;
          font-size: 11.5px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          padding: 12px 14px;
          text-align: left;
          border-bottom: 1px solid #eef2f7;
          white-space: nowrap;
        }
        .cp-table tbody td {
          padding: 12px 14px;
          vertical-align: middle;
          color: #1e293b;
          border-bottom: 1px solid #f1f5f9;
          background: #fff;
          transition: background .15s ease;
          max-width: 280px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .cp-table tbody tr:hover td { background: #f8fbff; }
        .cp-srno {
          display:inline-flex; align-items:center; justify-content:center;
          min-width: 32px; height: 28px;
          padding: 0 8px;
          border-radius: 8px;
          background: #f1f5f9;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        .cp-foot {
          display:flex; align-items:center; justify-content:space-between;
          padding: 14px 24px 18px;
          gap: 12px;
          flex-wrap: wrap;
          border-top: 1px solid #eef2f7;
          background: #fcfdff;
        }
        .cp-foot-left { display:flex; align-items:center; gap: 8px; color: #475569; font-size: 13px; flex-wrap: wrap; }
        .cp-foot-left select {
          height: 32px; padding: 0 10px;
          border: 1.5px solid #e2e8f0; border-radius: 8px; background: #fff;
        }
        .cp-trunc {
          display:inline-flex; align-items:center; gap:6px;
          margin-left: 12px;
          color: #b45309;
          font-size: 12.5px;
        }
        .cp-foot-right { display:flex; align-items:center; gap: 6px; }
        .cp-pg-btn {
          width: 34px; height: 34px;
          display:inline-flex; align-items:center; justify-content:center;
          background: #fff;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          cursor: pointer;
          color: #334155;
          transition: all .15s ease;
        }
        .cp-pg-btn:hover:not(:disabled) {
          background: #eff6ff;
          border-color: #bfdbfe;
          color: #1d4ed8;
        }
        .cp-pg-btn:disabled { opacity: .45; cursor: not-allowed; }
        .cp-pg-info { font-size: 13px; color: #475569; padding: 0 8px; }

        .cp-btn {
          display:inline-flex; align-items:center; gap: 6px;
          padding: 8px 14px;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all .2s ease;
          white-space: nowrap;
        }
        .cp-btn:disabled { opacity: .6; cursor: not-allowed; }
        .cp-btn svg { font-size: 16px; }
        .cp-btn-primary {
          color: #fff;
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 60%, #0ea5e9 100%);
          background-size: 200% 200%;
          background-position: 0% 50%;
          box-shadow: 0 8px 16px rgba(37, 99, 235, 0.25);
        }
        .cp-btn-primary:hover:not(:disabled) {
          background-position: 100% 50%;
          transform: translateY(-1px);
          box-shadow: 0 12px 22px rgba(37, 99, 235, 0.32);
        }
        .cp-btn-ghost {
          color: #1d4ed8;
          background: #fff;
          border: 1.5px solid #dbeafe;
        }
        .cp-btn-ghost:hover:not(:disabled) {
          background: #eff6ff;
          border-color: #93c5fd;
          color: #1e3a8a;
        }

        .cp-empty, .cp-loading, .cp-error-inline {
          padding: 48px 16px;
          text-align: center;
          color: #64748b;
        }
        .cp-empty { display:flex; flex-direction:column; align-items:center; gap: 6px; }
        .cp-empty svg { font-size: 56px; color: #cbd5e1; }
        .cp-empty h6 { margin: 6px 0 0; font-size: 16px; color: #334155; font-weight: 600; }
        .cp-empty p { margin: 0; font-size: 13px; }

        .cp-loading { padding: 22px 24px; text-align: left; }
        .cp-skel {
          height: 24px;
          margin-bottom: 12px;
          background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: cp-shimmer 1.4s linear infinite;
          border-radius: 8px;
        }
        @keyframes cp-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .cp-error-inline {
          display:flex; align-items: center; justify-content: center; gap: 14px; flex-wrap: wrap;
          padding: 28px 22px;
        }
        .cp-error-inline svg { font-size: 36px; color: #ef4444; }
        .cp-error-inline > div { text-align: left; max-width: 560px; }
        .cp-error-inline strong { font-size: 14.5px; color: #0f172a; }
        .cp-error-inline p { margin: 4px 0 4px; font-size: 13px; color: #64748b; word-break: break-word; }
        .cp-error-inline small { font-size: 12px; color: #94a3b8; }
        .cp-error-inline code {
          background: #f1f5f9; padding: 1px 6px; border-radius: 4px;
          font-size: 11.5px; color: #1d4ed8;
        }

        .cp-error-card {
          display:flex; align-items:center; gap: 14px; padding: 22px 24px; flex-wrap: wrap;
        }
        .cp-error-card > svg { font-size: 36px; color: #ef4444; }
        .cp-error-card h6 { margin: 0; font-size: 15px; color: #0f172a; font-weight: 600; }
        .cp-error-card p { margin: 2px 0 0; font-size: 13px; color: #64748b; }

        @media (max-width: 768px) {
          .cp-card-head { padding: 18px; }
          .cp-head-left h5 { font-size: 16px; }
          .cp-filters { padding: 14px 16px; }
          .cp-field-grow { grid-column: span 1; }
        }
      `}</style>
    </div>
  );
};

export default CopperLayer;